import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { Button } from '../components/ui/Button';
import { useLoginFlow } from './useLoginFlow';

export function RecoveryCodesPage() {
  const { state, dispatch } = useLoginFlow();
  const { authenticate } = useAuth();
  const navigate = useNavigate();

  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Guard: must arrive here from enrollment, which deposits pendingTokens in state.
  useEffect(() => {
    if (state.step !== 'recovery_codes_shown') {
      void navigate('/login', { replace: true });
    }
  }, [state.step, navigate]);

  if (state.step !== 'recovery_codes_shown') return null;

  const { recoveryCodes, pendingTokens, pendingUser } = state;
  const codesText = recoveryCodes.join('\n');

  const copyAll = () => {
    void navigator.clipboard.writeText(codesText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const download = () => {
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'botica-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleContinue = () => {
    dispatch({ type: 'AUTHENTICATED' });
    authenticate(pendingTokens, pendingUser);
    void navigate('/', { replace: true });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div>
        <p
          style={{
            margin: '0 0 6px',
            fontSize: 'var(--text-base)',
            fontWeight: 600,
            color: 'var(--text-primary)',
          }}
        >
          Recovery Codes
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            lineHeight: 'var(--leading-normal)',
          }}
        >
          Store them somewhere safe. Each code can only be used once and{' '}
          <strong style={{ color: 'var(--text-primary)' }}>won't be shown again.</strong>
        </p>
      </div>

      {/* ── Codes grid ───────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '6px 12px',
          padding: '16px',
          background: 'var(--warm-gray-50)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {recoveryCodes.map((code) => (
          <span
            key={code}
            style={{
              fontFamily: 'monospace',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-primary)',
              letterSpacing: '0.08em',
            }}
          >
            {code}
          </span>
        ))}
      </div>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="secondary" size="md" onClick={copyAll} style={{ flex: 1 }}>
          {copied ? 'Copied!' : 'Copy all'}
        </Button>
        <Button variant="secondary" size="md" onClick={download} style={{ flex: 1 }}>
          Download .txt
        </Button>
      </div>

      {/* ── Confirmation checkbox ─────────────────────────────────────────── */}
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 'var(--leading-normal)' }}>
          I've stored these codes somewhere safe — I understand they won't be shown again.
        </span>
      </label>

      <Button
        type="button"
        variant="primary"
        size="lg"
        disabled={!confirmed}
        onClick={handleContinue}
        style={{ width: '100%' }}
      >
        Continue to dashboard
      </Button>
    </div>
  );
}
